import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import AppEditor from '../components/SDSEditorApp';
import { initialSDSData, STORAGE_KEY } from '../data/initialData';
import { ArrowRight, Save, Loader2, ShieldCheck, Home } from 'lucide-react';
import { toast } from 'sonner';

// Admin variant: loads any SDS via /admin/sds/{id} and updates via PUT /admin/sds/{id}
export default function AdminSDSEditorPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const dir = isEn ? 'ltr' : 'rtl';
  const nav = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [factoryName, setFactoryName] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/sds/${id}`);
        if (cancel) return;
        const data = r.data?.data || initialSDSData;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        setTitle(r.data?.title || 'تقرير SDS');
        setFactoryName(r.data?.factoryName || '');
      } catch {
        toast.error('تعذر تحميل التقرير');
        nav('/admin');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id, nav]);

  const save = async () => {
    setSaving(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : initialSDSData;
      await axios.put(`${API}/admin/sds/${id}`, {
        title: title || data?.facility?.name || 'تقرير SDS',
        data,
      });
      toast.success('تم حفظ التغييرات');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تعذر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <Loader2 className="animate-spin text-[#1a4d80]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" dir={dir}>
      <div className="no-print sticky top-0 z-40 bg-emerald-700 text-white px-4 py-2 flex items-center justify-between shadow">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Navigation cluster: Back + Home */}
          <div className="flex items-center bg-white/10 rounded-md overflow-hidden border border-white/20" data-testid="admin-sds-nav-cluster">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => nav(-1)}
              className="text-white hover:bg-white/15 rounded-none h-9 px-3 border-0"
              data-testid="admin-sds-back-btn"
              title={t('common.back')}
            >
              <ArrowRight size={16} className={isEn ? 'rotate-180 mr-1' : 'ml-1'} />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </Button>
            <div className="w-px h-5 bg-white/30"/>
            <Link to="/admin">
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/15 rounded-none h-9 px-3 border-0"
                data-testid="admin-sds-home-btn"
                title={t('common.home')}
              >
                <Home size={16} className="ml-1" />
                <span className="hidden sm:inline">{t('common.home')}</span>
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={16}/>
            <span className="text-xs">{isEn ? 'Admin mode — editing SDS for' : 'وضع المشرف — يحرر تقرير لـ'} <b>{factoryName}</b></span>
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isEn ? 'Report title' : 'عنوان التقرير'}
            className="bg-white/10 border border-white/20 rounded px-3 py-1 text-sm placeholder-white/60 focus:outline-none focus:bg-white/20"
            data-testid="admin-sds-title-input"
          />
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="admin-sds-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin ml-1" /> : <Save size={14} className="ml-1" />} {isEn ? 'Save' : 'حفظ'}
        </Button>
      </div>
      <AppEditor />
    </div>
  );
}
